/**
 * EmployeeCaseDetailPanel - one of a person's assigned matters, read-only.
 *
 * TWO HOSTS, NO CHROME OF ITS OWN. It is the `detail` pane of the person
 * surface, and it is still the `employeeCaseDetail` side panel the external
 * collaborator page opens. Both hosts already draw a glass box with a header
 * and a close button, so the `bg-white dark:bg-neutral-900 … rounded
 * shadow-md` card this used to draw was a card inside a card, and the second
 * of two stacked headers.
 *
 * Colours are semantic tokens now, so it flips with the theme without a
 * single `dark:` utility.
 *
 * TWO ROW SHAPES. The person page feeds it `POST /matters/get_assigned_matters`
 * - the platform's own matter (`title`, `uuid`, `status_name`, `type`,
 * `matter_clients`) - while the external collaborator page still feeds it the
 * legacy capitalised one (`CaseName`, `case_status`, `ClientName`). It reads
 * either, so neither host has to translate.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../shared/TailwindComponents/Button';
import Badge from '../../shared/TailwindComponents/Badge';
import { Field, Well } from '../../shared/brand/Field';
import { ContextSection } from '../../shared/brand/ContextPane';
import { formatDateTime } from '../../../utils/functions';

interface Props {
  caseData: any;
  onRequestAccess?: () => void;
}

const EmployeeCaseDetailPanel: React.FC<Props> = ({ caseData, onRequestAccess }) => {
  const { t } = useTranslation();

  if (!caseData) {
    return (
      <p className="p-4 text-sm text-fg-muted text-center">{t('No case data found.')}</p>
    );
  }

  const progressArray: any[] = caseData.progress || [];

  const pick = (...values: any[]) =>
    values.find((v) => v !== undefined && v !== null && v !== '');

  const title = pick(caseData.title, caseData.CaseName);
  const type = pick(caseData.category_name, caseData.type, caseData.CaseType);
  const status = pick(caseData.status_name, caseData.stage_name, caseData.case_status);
  const clientName = pick(
    caseData.matter_clients?.clients?.[0]?.name,
    caseData.ClientName
  );
  const description = pick(caseData.description, caseData.fields?.issue_description);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main px-3 py-3 space-y-4">
        <div>
          <h3 className="text-base font-medium text-fg-strong">
            {title || t('Untitled matter')}
          </h3>
          <div className="flex items-center gap-2 flex-wrap mt-2">
            {type && <Badge color="blue">{t(type)}</Badge>}
            {status && (
              <Badge color={String(status).toLowerCase() === 'closed' ? 'gray' : 'green'}>
                {t(status)}
              </Badge>
            )}
            {caseData.priority && <Badge color="gray">{t(caseData.priority)}</Badge>}
          </div>
        </div>

        <Well className="space-y-3">
          <Field
            label={t('Created')}
            value={caseData.created_at ? formatDateTime(caseData.created_at) : undefined}
            emptyText="-"
          />
          <Field
            label={t('Lawyer Rate')}
            value={
              typeof caseData.LawyerHourlyRate === 'number'
                ? `$${caseData.LawyerHourlyRate.toFixed(2)}/hr`
                : undefined
            }
            emptyText="-"
          />
          <Field
            label={t('Due')}
            value={caseData.due_date ? formatDateTime(caseData.due_date) : undefined}
            emptyText="-"
          />
          <Field label={t('Jurisdiction')} value={caseData.jurisdiction} emptyText="-" />
          <Field label={t('Client')} value={clientName} emptyText="-" />
        </Well>

        {progressArray.length > 0 && (
          <div>
            <ContextSection>{t('Case Progress')}</ContextSection>
            <ul className="space-y-1.5">
              {progressArray.map((step: any, idx: number) => (
                <li key={idx} className="flex items-center gap-2">
                  <Badge color={step.status === 'Pending' ? 'gray' : 'green'}>{t(step.step)}</Badge>
                  <span className="text-sm text-fg-muted">{t(step.status)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {description && (
          <div>
            <ContextSection>{t('Description')}</ContextSection>
            <p className="text-sm text-fg-body leading-relaxed whitespace-pre-wrap">
              {description}
            </p>
          </div>
        )}
      </div>

      {/* Only offered when the host has somewhere for it to go. It used to
          render always and call an `onRequestAccess` that was frequently
          undefined, so the button did nothing at all. */}
      {onRequestAccess && (
        <div className="flex-shrink-0 px-3 py-2 border-t border-hairline-soft">
          <Button variant="primary" size="small" className="w-full" onClick={onRequestAccess}>
            {t('Request Full Access')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default EmployeeCaseDetailPanel;
